// Applies legible Chart.js defaults for chartkick charts in dark mode.
//
// The app toggles a `.dark` class on <html>. We read that class and set
// `Chartkick.options` (grid / tick / legend colors), then redraw existing
// charts. Re-applied on DOMContentLoaded, turbo:load, a `darkmode:changed`
// custom event, and via a MutationObserver watching the <html> class attr.

const TICK_COLOR_DARK = "#9CA3AF" // gray-400
const TICK_COLOR_LIGHT = "#6B7280" // gray-500
const GRID_COLOR_DARK = "rgba(255, 255, 255, 0.08)"
const GRID_COLOR_LIGHT = "rgba(0, 0, 0, 0.06)"

function isDark() {
  return document.documentElement.classList.contains("dark")
}

export function applyChartkickTheme() {
  if (typeof window === "undefined" || !window.Chartkick) return

  const dark = isDark()
  const tickColor = dark ? TICK_COLOR_DARK : TICK_COLOR_LIGHT
  const gridColor = dark ? GRID_COLOR_DARK : GRID_COLOR_LIGHT

  window.Chartkick.options = {
    library: {
      color: tickColor,
      borderColor: gridColor,
      plugins: {
        legend: {
          labels: { color: tickColor }
        }
      },
      scales: {
        x: {
          ticks: { color: tickColor },
          grid: { color: gridColor }
        },
        y: {
          ticks: { color: tickColor },
          grid: { color: gridColor }
        }
      }
    }
  }

  // Redraw any already-rendered charts so the new theme takes effect.
  if (typeof window.Chartkick.eachChart === "function") {
    try {
      window.Chartkick.eachChart((chart) => {
        if (chart && typeof chart.redraw === "function") chart.redraw()
      })
    } catch (e) {
      // Defensive: never let theming break the page.
    }
  }
}

function setup() {
  applyChartkickTheme()

  // Re-apply whenever the .dark class on <html> changes (self-contained).
  if (typeof MutationObserver !== "undefined" && !setup.observed) {
    setup.observed = true
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class") {
          applyChartkickTheme()
          break
        }
      }
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    })
  }
}

// Expose so the dark-mode toggle controller can call it directly.
if (typeof window !== "undefined") {
  window.applyChartkickTheme = applyChartkickTheme
}

document.addEventListener("DOMContentLoaded", setup)
document.addEventListener("turbo:load", applyChartkickTheme)
document.addEventListener("darkmode:changed", applyChartkickTheme)
